import { useCallback, useEffect, useState } from "react";
import { MdArrowDownward, MdArrowUpward, MdClose, MdSearch } from "react-icons/md";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import { statusLabel, statusTone } from "components/markets/marketActions";
import { apiGet, apiPut, buildQueryString } from "lib/apiClient";
import { useAuth } from "contexts/AuthContext";
import { CURATED_MAX, addCurated, moveItem, partnerPageUrls, removeCurated, sameOrder } from "lib/partnerPages";

const iconButton =
  "flex h-8 w-8 items-center justify-center rounded-sm border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40";

function saveErrorMessage(err) {
  switch (err?.code) {
    case "INVALID_CURATED_MARKET":
      return "One of these markets can no longer be shown (it may have closed or been unpublished). Remove it and save again.";
    case "FORBIDDEN":
      return "Only a partner administrator can change the curated markets.";
    default:
      return err?.message || "Could not save the curated markets. Try again.";
  }
}

function MarketLine({ market, children }) {
  return (
    <div className="flex items-center gap-3">
      {market.imageUrl ? (
        <img src={market.imageUrl} alt="" className="h-9 w-9 flex-shrink-0 rounded-md object-cover" />
      ) : (
        <div className="h-9 w-9 flex-shrink-0 rounded-md bg-accent" />
      )}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-semibold text-navy-700 dark:text-white">{market.question}</p>
        {market.status && (
          <div className="mt-0.5">
            <StatusBadge label={statusLabel(market.status)} tone={statusTone(market.status)} />
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

export default function PartnerCuratedPage() {
  const { portal } = useAuth();
  const canEdit = Boolean(portal?.canManage);
  const urls = partnerPageUrls(portal?.partner?.slug);

  const [saved, setSaved] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const result = await apiGet("/portal/partner/curated-markets");
      const markets = [...(result.markets || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      setSaved(markets);
      setList(markets);
    } catch (err) {
      setLoadError(err.message || "Could not load your curated markets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      setCandidatesLoading(true);
      apiGet(`/portal/partner/curated-markets/candidates${buildQueryString({ search: search.trim() })}`)
        .then((result) => {
          if (cancelled) return;
          setCandidates(result.markets || []);
          setCandidatesError(null);
        })
        .catch((err) => {
          if (!cancelled) setCandidatesError(err.message || "Could not search markets.");
        })
        .finally(() => {
          if (!cancelled) setCandidatesLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, canEdit]);

  const dirty = !sameOrder(list, saved);
  const full = list.length >= CURATED_MAX;

  function change(next) {
    setList(next);
    setNotice(null);
    setSaveError(null);
  }

  async function save() {
    setBusy(true);
    setSaveError(null);
    setNotice(null);
    try {
      const result = await apiPut("/portal/partner/curated-markets", { marketIds: list.map((m) => m.marketId) });
      const markets = [...(result?.markets || list)].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      setSaved(markets);
      setList(markets);
      setNotice("Saved. Your page shows these markets in this order.");
    } catch (err) {
      setSaveError(saveErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-5">
        <p className="text-2xl font-semibold text-navy-700 dark:text-white">Curated markets</p>
        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
          Pick up to {CURATED_MAX} of {portal?.tenant?.name || "Wasaa"}'s main markets to feature on your page, in the order you want. A market that closes drops off your page by itself.
          {urls && (
            <>
              {" "}
              <a href={urls.page} target="_blank" rel="noreferrer" className="font-semibold text-brand-500 hover:underline">
                View your page
              </a>
            </>
          )}
        </p>
        {!canEdit && <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">You can view this list. Only a partner administrator can change it.</p>}
      </div>

      {notice && <div className="mb-4 rounded-xl bg-green-50 p-3 text-sm font-medium text-green-700 dark:bg-green-500/10 dark:text-green-300">{notice}</div>}

      <div className={`grid grid-cols-1 gap-5 ${canEdit ? "xl:grid-cols-2" : ""}`}>
        <Card extra="p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-lg font-semibold text-navy-700 dark:text-white">On your page</p>
            <span className="text-sm text-gray-500">
              {list.length} / {CURATED_MAX}
            </span>
          </div>
          {loadError && (
            <div className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">
              {loadError}{" "}
              <button type="button" onClick={load} className="font-semibold underline">
                Retry
              </button>
            </div>
          )}
          {loading ? (
            <div className="mt-4 h-40 animate-pulse rounded-md bg-accent" />
          ) : list.length === 0 ? (
            <p className="mt-4 rounded-xl border border-dashed border-border p-6 text-center text-sm text-gray-500">
              {canEdit ? "No curated markets yet. Add some from the search." : "No curated markets yet."}
            </p>
          ) : (
            <ol className="mt-4 divide-y divide-gray-100 dark:divide-white/5">
              {list.map((m, i) => (
                <li key={m.marketId} className="flex items-center gap-3 py-2">
                  <span className="num w-5 shrink-0 text-right text-sm font-semibold text-gray-400">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <MarketLine market={m}>
                      {canEdit && (
                        <div className="flex shrink-0 items-center gap-1">
                          <button type="button" aria-label={`Move ${m.question} up`} disabled={i === 0 || busy} onClick={() => change(moveItem(list, i, -1))} className={iconButton}>
                            <MdArrowUpward />
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${m.question} down`}
                            disabled={i === list.length - 1 || busy}
                            onClick={() => change(moveItem(list, i, 1))}
                            className={iconButton}
                          >
                            <MdArrowDownward />
                          </button>
                          <button type="button" aria-label={`Remove ${m.question}`} disabled={busy} onClick={() => change(removeCurated(list, m.marketId))} className={`${iconButton} hover:text-red-500`}>
                            <MdClose />
                          </button>
                        </div>
                      )}
                    </MarketLine>
                  </div>
                </li>
              ))}
            </ol>
          )}
          {saveError && <p className="mt-3 text-sm font-medium text-red-500">{saveError}</p>}
          {canEdit && (
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={save} disabled={!dirty || busy} className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                {busy ? "Saving…" : "Save"}
              </button>
              {dirty && !busy && (
                <button
                  type="button"
                  onClick={() => change(saved)}
                  className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white"
                >
                  Discard changes
                </button>
              )}
            </div>
          )}
        </Card>

        {canEdit && (
          <Card extra="p-4 sm:p-5">
            <p className="text-lg font-semibold text-navy-700 dark:text-white">Add markets</p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Open, public main markets. Partner markets, yours included, can't be curated.</p>
            <div className="relative mt-3">
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search main markets…"
                aria-label="Search main markets"
                className="h-10 w-full rounded-sm border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/50"
              />
            </div>
            {full && <p className="mt-2 text-xs font-medium text-amber-600">Your list is full. Remove a market to add another.</p>}
            {candidatesError && <p className="mt-3 text-sm font-medium text-red-500">{candidatesError}</p>}
            <div className="mt-3 max-h-[32rem] overflow-y-auto">
              {candidatesLoading && candidates.length === 0 ? (
                <div className="h-32 animate-pulse rounded-md bg-accent" />
              ) : candidates.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">{search.trim() ? "No main markets match that search." : "No open main markets right now."}</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-white/5">
                  {candidates.map((c) => {
                    const added = list.some((m) => m.marketId === c.marketId);
                    return (
                      <li key={c.marketId} className="py-2">
                        <MarketLine market={c}>
                          <button
                            type="button"
                            disabled={added || full || busy}
                            onClick={() => change(addCurated(list, c))}
                            className="shrink-0 rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400/10"
                          >
                            {added ? "Added" : "Add"}
                          </button>
                        </MarketLine>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
